import { useCallback, useMemo, useState } from 'react';
import type { Member } from '../lib/protocol.js';
import { useJam } from '../lib/useJam.js';
import { AddBar } from './AddBar.js';
import { PlayerPanel } from './PlayerPanel.js';
import { Queue } from './Queue.js';

type Props = {
  roomCode: string;
  name: string;
  onLeave: () => void;
  onRename: (name: string) => void;
};

export function RoomView({ roomCode, name, onLeave, onRename }: Props) {
  const jam = useJam(roomCode, name);
  /**
   * 音を出すかはこの端末のローカルな設定。ブラウザの自動再生制限があるため
   * 既定は off で、必ずユーザーの操作を起点にプレイヤーを作る。
   */
  const [audioOn, setAudioOn] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggleAudio = useCallback(
    (enabled: boolean) => {
      setAudioOn(enabled);
      jam.actions.setAudio(enabled);
    },
    [jam.actions],
  );

  const memberById = useMemo(() => {
    const map = new Map<string, Member>();
    for (const m of jam.state?.members ?? []) map.set(m.id, m);
    return map;
  }, [jam.state?.members]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${location.origin}/r/${roomCode}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const leave = () => {
    jam.actions.leave();
    onLeave();
  };

  if (!jam.state) {
    return (
      <main className="room room-loading">
        <p className="hint">
          {jam.status === 'reconnecting' ? '再接続中...' : `部屋 ${roomCode} に接続中...`}
        </p>
        {jam.notice?.level === 'error' && (
          <>
            <p className="error">{jam.notice.message}</p>
            <button className="btn" onClick={onLeave}>
              入口に戻る
            </button>
          </>
        )}
      </main>
    );
  }

  const state = jam.state;
  const online = state.members.filter((m) => m.online);
  const isHost = jam.me?.id === state.hostId;

  return (
    <main className="room">
      <header className="room-head">
        <div className="room-code">
          <span className="room-code-label">部屋コード</span>
          <strong>{state.code}</strong>
        </div>
        <div className="room-head-actions">
          <button className="btn btn-small" onClick={copyLink}>
            {copied ? 'コピーしました' : 'リンクをコピー'}
          </button>
          <button className="btn btn-small" onClick={leave}>
            退出
          </button>
        </div>
      </header>

      <div className={`conn conn-${jam.status}`}>
        {jam.status === 'ready' ? (
          <>
            接続中 · 時刻ずれ {jam.clockOffsetMs > 0 ? '+' : ''}
            {jam.clockOffsetMs}ms
          </>
        ) : jam.status === 'reconnecting' ? (
          '再接続中...'
        ) : (
          '接続中...'
        )}
      </div>

      <ul className="members">
        {state.members.map((m) => (
          <li
            key={m.id}
            className={`member ${m.online ? '' : 'member-offline'}`}
            title={
              [
                m.online ? 'オンライン' : 'オフライン（曲は退避中）',
                m.id === state.hostId ? 'ホスト' : null,
                m.id === state.leaderId ? '再生の基準端末' : null,
                m.audio ? '音を出している' : null,
              ]
                .filter(Boolean)
                .join(' / ')
            }
          >
            <span className="dot" style={{ background: m.color }} />
            {m.name}
            {m.id === state.hostId && <span className="tag">ホスト</span>}
            {m.audio && <span className="tag tag-audio">♪</span>}
          </li>
        ))}
      </ul>

      <PlayerPanel
        jam={jam}
        audioOn={audioOn}
        onToggleAudio={toggleAudio}
        memberById={memberById}
      />

      <AddBar jam={jam} />

      <Queue state={state} jam={jam} memberById={memberById} />

      <footer className="room-foot">
        <p className="hint hint-small">
          {online.length} 人が参加中
          {isHost && ' · あなたはホストです'}
        </p>
        <label className="field field-inline">
          <span className="field-label">名前を変える</span>
          <input
            className="input input-small"
            defaultValue={name}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (!next || next === name) return;
              onRename(next);
              jam.actions.rename(next);
            }}
            maxLength={24}
          />
        </label>
      </footer>

      {jam.notice && (
        <div className={`toast toast-${jam.notice.level}`}>{jam.notice.message}</div>
      )}
    </main>
  );
}
